/**
 * MiniMax AI API 服务（支持 M2.5 / M2.7 等）
 * 提供AI对话、分析等功能
 */

import { extractMinimaxAssistantContent } from '../utils/minimaxResponseParse.js';
import { resolveMinimaxChatCompletionUrl } from '../utils/minimaxEndpoint.js';

/** 默认对话模型（平台已升级至 2.7 系列时可优先选用） */
export const MINIMAX_DEFAULT_MODEL = 'MiniMax-M2.7';

// API配置（支持 MiniMax-M2.7、M2.5、Lightning、Text-01 等）
const MINIMAX_CONFIG = {
  baseUrl: 'https://api.minimaxi.com/v1',
  model: MINIMAX_DEFAULT_MODEL,
  visionModel: 'MiniMax-VL-01', // 视觉理解模型
  maxTokens: 4096,
};

// 功能开关配置
let featureConfig = {
  webSearch: false, // 联网搜索
  imageUnderstanding: true, // 图片理解
};

// 加载功能配置
const loadFeatureConfig = () => {
  try {
    const saved = localStorage.getItem('minimax_feature_config');
    if (saved) {
      featureConfig = { ...featureConfig, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.warn('加载功能配置失败:', e);
  }
};

// 保存功能配置
const saveFeatureConfig = () => {
  try {
    localStorage.setItem('minimax_feature_config', JSON.stringify(featureConfig));
  } catch (e) {
    console.warn('保存功能配置失败:', e);
  }
};

/**
 * 获取功能配置
 */
export const getFeatureConfig = () => ({ ...featureConfig });

/**
 * 设置功能配置
 */
export const setFeatureConfig = (config) => {
  featureConfig = { ...featureConfig, ...config };
  saveFeatureConfig();
};

// 初始化加载功能配置
loadFeatureConfig();

// 使用额度追踪
let usageStats = {
  totalPrompts: 0,
  totalTokens: 0,
  lastReset: new Date().toISOString(),
  history: [], // 最近的使用记录
};

// 历史对话记录
let conversationHistory = [];

// 从本地存储加载使用统计
const loadUsageStats = () => {
  try {
    const saved = localStorage.getItem('minimax_usage_stats');
    if (saved) {
      usageStats = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('加载AI使用统计失败:', e);
  }
};

// 保存使用统计到本地存储
const saveUsageStats = () => {
  try {
    localStorage.setItem('minimax_usage_stats', JSON.stringify(usageStats));
  } catch (e) {
    console.warn('保存AI使用统计失败:', e);
  }
};

// 加载历史对话
const loadConversationHistory = () => {
  try {
    const saved = localStorage.getItem('minimax_conversation_history');
    if (saved) {
      conversationHistory = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('加载历史对话失败:', e);
  }
};

// 保存历史对话
const saveConversationHistory = () => {
  try {
    localStorage.setItem('minimax_conversation_history', JSON.stringify(conversationHistory));
  } catch (e) {
    console.warn('保存历史对话失败:', e);
  }
};

// 添加对话到历史记录
const addToHistory = (type, prompt, response, tokens, messages = null) => {
  const record = {
    id: `conv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    timestamp: new Date().toISOString(),
    type, // 'market' | 'risk' | 'corn' | 'internal' | 'general' | 'search' | 'image'
    prompt: prompt, // 保存完整prompt
    promptPreview: prompt.substring(0, 200) + (prompt.length > 200 ? '...' : ''), // 预览版本
    response: response.substring(0, 500) + (response.length > 500 ? '...' : ''), // 预览版本
    fullResponse: response, // 完整回复
    tokens,
    messages: messages || null, // 完整的对话消息数组
  };
  conversationHistory.unshift(record);
  // 只保留最近50条
  if (conversationHistory.length > 50) {
    conversationHistory = conversationHistory.slice(0, 50);
  }
  saveConversationHistory();
};

/**
 * 获取历史对话
 */
export const getConversationHistory = () => {
  return [...conversationHistory];
};

/**
 * 清空历史对话
 */
export const clearConversationHistory = () => {
  conversationHistory = [];
  saveConversationHistory();
};

/**
 * 删除单条历史对话
 */
export const deleteConversation = (id) => {
  conversationHistory = conversationHistory.filter(c => c.id !== id);
  saveConversationHistory();
};

/**
 * 添加完整对话到历史记录（包含消息数组）
 */
export const addConversationToHistory = (type, messages, tokens = 0) => {
  if (!messages || messages.length === 0) return;
  
  // 提取用户消息和助手回复
  const userMessage = messages.find(m => m.role === 'user');
  const assistantMessage = messages.find(m => m.role === 'assistant');
  
  if (!userMessage || !assistantMessage) return;
  
  const prompt = typeof userMessage.content === 'object' 
    ? (userMessage.content.text || '') 
    : (userMessage.content || '');
  const response = assistantMessage.content || '';
  
  addToHistory(type, prompt, response, tokens, messages);
};

// 初始化加载
loadUsageStats();
loadConversationHistory();

// ========== API 密钥加密存储 ==========
// 生成或获取设备唯一标识作为加密种子
const getDeviceSeed = () => {
  let seed = localStorage.getItem('_device_seed');
  if (!seed) {
    seed = btoa(Date.now().toString(36) + Math.random().toString(36).slice(2));
    localStorage.setItem('_device_seed', seed);
  }
  return seed;
};

// 简单的 XOR 加密
const xorEncrypt = (text, key) => {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
};

// 加密 API Key
const encryptApiKey = (apiKey) => {
  if (!apiKey) return '';
  try {
    const seed = getDeviceSeed();
    const encrypted = xorEncrypt(apiKey, seed);
    return btoa(unescape(encodeURIComponent(encrypted)));
  } catch (e) {
    return apiKey;
  }
};

// 解密 API Key
const decryptApiKey = (encryptedKey) => {
  if (!encryptedKey) return '';
  
  // 检查是否是旧的明文格式
  if (/^[a-zA-Z0-9-_]+$/.test(encryptedKey)) {
    return encryptedKey;
  }
  
  try {
    const seed = getDeviceSeed();
    const encrypted = decodeURIComponent(escape(atob(encryptedKey)));
    return xorEncrypt(encrypted, seed);
  } catch (e) {
    return encryptedKey; // 旧格式兼容
  }
};

/**
 * 获取API密钥（解密）
 */
const getApiKey = () => {
  const encrypted = localStorage.getItem('minimax_api_key') || '';
  return decryptApiKey(encrypted);
};

/**
 * 设置API密钥（加密存储）
 */
export const setApiKey = (key) => {
  const encrypted = encryptApiKey(key);
  localStorage.setItem('minimax_api_key', encrypted);
};

/**
 * 检查API密钥是否已配置
 */
export const hasApiKey = () => {
  return !!getApiKey();
};

/** 供「保存到服务器」等场景读取当前浏览器内已解密的 Key（与 callMiniMaxAPI 使用同源） */
export const getMinimaxApiKey = () => getApiKey();

/**
 * 将 MiniMax 配置写入服务端（server/data/ai-minimax-config.json，供舆情情感分析、后端调用等使用）。
 * 需要管理员登录（Bearer token 或 Cookie）。
 * @param {{ apiKey?: string, groupId?: string, model?: string, baseUrl?: string }} params 未传 apiKey 时使用本地已保存的 Key
 */
export async function saveMinimaxToServerDatabase(params = {}) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  const apiKey = String(params.apiKey != null ? params.apiKey : getApiKey()).trim();
  if (!apiKey) {
    throw new Error('请先填写并保存 API Key');
  }
  const groupId = String(params.groupId ?? '').trim();
  const model = String(params.model || MINIMAX_CONFIG.model || MINIMAX_DEFAULT_MODEL).trim() || MINIMAX_DEFAULT_MODEL;
  let baseUrl = String(params.baseUrl || `${MINIMAX_CONFIG.baseUrl}`).trim().replace(/\/+$/, '');
  if (!baseUrl) baseUrl = 'https://api.minimaxi.com/v1';

  const res = await fetch('/api/ai-config/minimax', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ apiKey, groupId, model, baseUrl }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `保存失败（HTTP ${res.status}），请确认已使用管理员账号登录`);
  }
  return data;
}

/**
 * 调用MiniMax API
 * @param {string} prompt - 用户提示
 * @param {object} options - 可选配置
 * @returns {Promise<{success: boolean, content?: string, error?: string, usage?: object}>}
 */
export const callMiniMaxAPI = async (prompt, options = {}) => {
  const apiKey = getApiKey();
  if (!apiKey) {
    return { success: false, error: '请先配置MiniMax API密钥' };
  }

  const {
    systemPrompt = '你是一个专业的数据分析助手，擅长财务分析、风险评估和决策支持。请用简洁专业的语言回答问题。',
    temperature = 0.7,
    maxTokens = MINIMAX_CONFIG.maxTokens,
    model = MINIMAX_CONFIG.model,
  } = options;

  try {
    const chatUrl = resolveMinimaxChatCompletionUrl(MINIMAX_CONFIG.baseUrl);
    const response = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt },
        ],
        temperature,
        max_completion_tokens: maxTokens,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API请求失败: ${response.status}`);
    }

    const data = await response.json();
    
    // 更新使用统计
    const tokensUsed = data.usage?.total_tokens || 0;
    usageStats.totalPrompts += 1;
    usageStats.totalTokens += tokensUsed;
    usageStats.history.push({
      timestamp: new Date().toISOString(),
      tokens: tokensUsed,
      promptLength: prompt.length,
    });
    // 只保留最近100条记录
    if (usageStats.history.length > 100) {
      usageStats.history = usageStats.history.slice(-100);
    }
    saveUsageStats();

    const content = extractMinimaxAssistantContent(data);
    
    // 添加到历史记录
    addToHistory(options.analysisType || 'general', prompt, content, tokensUsed);
    
    return {
      success: true,
      content,
      usage: {
        promptTokens: data.usage?.prompt_tokens || 0,
        completionTokens: data.usage?.completion_tokens || 0,
        totalTokens: tokensUsed,
      },
    };
  } catch (error) {
    console.error('MiniMax API调用失败:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 调用MiniMax API（带实时联网搜索）
 * 使用本地实时搜索服务，而不是模型内置插件
 * @param {string} prompt - 用户提示
 * @param {object} options - 可选配置
 */
export const callMiniMaxAPIWithSearch = async (prompt, options = {}) => {
  const apiKey = getApiKey();
  if (!apiKey) {
    return { success: false, error: '请先配置MiniMax API密钥' };
  }

  // 获取当前日期
  const today = new Date();
  const dateStr = `${today.getFullYear()}年${today.getMonth() + 1}月${today.getDate()}日`;

  const {
    model = MINIMAX_CONFIG.model,
    systemPrompt = `你是一个专业的数据分析助手，具备实时联网搜索能力。今天是${dateStr}。

重要提示：
1. 你将收到实时联网搜索结果，必须基于这些最新数据回答
2. 不要使用训练数据中的旧信息，除非搜索结果中没有相关信息
3. 如果搜索结果中的数据不是最新的，请如实说明数据的实际时间
4. 在回答中明确标注数据来源和搜索时间
5. 建议用户访问官方网站获取最新信息
6. 用简洁专业的语言回答`,
    temperature = 0.7,
    maxTokens = MINIMAX_CONFIG.maxTokens,
  } = options;

  try {
    // 先执行实时联网搜索
    let searchContext = '';
    let searchMetadata = null;
    
    if (window.desktopAPI?.webSearch) {
      try {
        console.log('[MiniMax实时搜索] 执行搜索:', prompt);
        const searchResult = await window.desktopAPI.webSearch(prompt);
        searchMetadata = {
          success: searchResult.success,
          source: searchResult.source || '未知',
          searchTime: searchResult.searchTime || new Date().toISOString(),
          resultCount: searchResult.results?.length || 0,
        };
        
        if (searchResult.success && searchResult.results?.length > 0) {
          const searchTimeStr = new Date(searchResult.searchTime || Date.now()).toLocaleString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
          
          searchContext = `\n\n【⚠️ 重要提示：以下是实时联网搜索结果，请务必基于这些最新数据回答，不要使用训练数据中的旧信息】\n` +
            `【搜索时间：${searchTimeStr}】\n` +
            `【数据来源：${searchResult.source || '实时搜索'}】\n\n` +
            `【实时搜索结果】\n` +
            searchResult.results.map((r, i) => 
              `${i + 1}. 【${r.title}】\n   ${r.snippet}${r.url ? `\n   来源：${r.url}` : ''}`
            ).join('\n\n') +
            `\n\n【回答要求】\n` +
            `1. 必须基于上述实时搜索结果回答，不要使用训练数据中的信息\n` +
            `2. 如果搜索结果中没有相关信息，请明确说明"根据实时搜索结果，未找到相关信息"\n` +
            `3. 在回答中标注数据来源和时间\n` +
            `4. 如果搜索结果中的数据不是最新的，请如实说明数据的实际时间\n` +
            `5. 建议用户访问官方网站获取最新信息`;
          
          console.log('[MiniMax实时搜索] 搜索到', searchResult.results.length, '条结果');
        } else {
          searchContext = `\n\n【⚠️ 实时联网搜索提示】\n` +
            `搜索时间：${new Date().toISOString()}\n` +
            `未能获取到实时搜索结果。请基于你的知识回答，但请明确说明这是基于训练数据的回答，可能不是最新信息。`;
        }
      } catch (e) {
        console.warn('[MiniMax实时搜索] 搜索失败:', e);
        searchContext = `\n\n【⚠️ 实时联网搜索提示】\n` +
          `搜索时间：${new Date().toISOString()}\n` +
          `实时搜索功能暂时不可用。请基于你的知识回答，但请明确说明这是基于训练数据的回答，可能不是最新信息。`;
      }
    }

    // 构建增强的提示
    const enhancedPrompt = `【查询时间：${dateStr}】\n\n${prompt}${searchContext}`;

    // 使用普通 API 调用，不使用插件（因为我们已经有了实时搜索结果）
    const requestBody = {
      model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: enhancedPrompt },
      ],
      temperature,
      max_completion_tokens: maxTokens,
    };

    console.log('[MiniMax实时搜索] API请求:', JSON.stringify(requestBody, null, 2));

    const chatUrl = resolveMinimaxChatCompletionUrl(MINIMAX_CONFIG.baseUrl);
    const response = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('联网搜索API错误:', errorData);
      throw new Error(errorData.base_resp?.status_msg || errorData.message || `API请求失败: ${response.status}`);
    }

    const data = await response.json();
    console.log('[MiniMax实时搜索] API响应:', JSON.stringify(data, null, 2));
    
    const tokensUsed = data.usage?.total_tokens || 0;
    
    // 更新统计
    usageStats.totalPrompts += 1;
    usageStats.totalTokens += tokensUsed;
    saveUsageStats();

    let content = extractMinimaxAssistantContent(data);
    if (!content && data.choices?.[0]) {
      content = JSON.stringify(data.choices[0], null, 2);
    }

    if (!content) {
      console.warn('[MiniMax实时搜索] 返回空内容，原始响应:', data);
      content = '实时搜索完成，但未获取到有效内容。请尝试重新提问或检查网络连接。';
    }

    // 添加时效性提示（如果使用了实时搜索）
    if (searchMetadata && searchMetadata.success) {
      const searchTimeStr = new Date(searchMetadata.searchTime).toLocaleString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      content += `\n\n---\n📅 实时搜索时间：${searchTimeStr}\n🔍 数据来源：${searchMetadata.source}\n⚠️ 提示：以上信息基于实时联网搜索结果，建议访问官方网站获取最新信息。`;
    } else {
      content += `\n\n---\n⚠️ 提示：实时搜索功能暂时不可用，以上回答基于训练数据，可能不是最新信息。建议访问官方网站获取最新信息。`;
    }

    addToHistory('search', prompt, content, tokensUsed);
    
    return {
      success: true,
      content,
      searchResults: data.web_search || data.choices?.[0]?.message?.tool_calls || [],
      usage: { totalTokens: tokensUsed },
    };
  } catch (error) {
    console.error('联网搜索失败:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 调用MiniMax视觉理解API（图片分析）
 * @param {string} imageBase64 - Base64编码的图片
 * @param {string} prompt - 用户提示
 * @param {object} options - 可选配置
 */
export const analyzeImage = async (imageBase64, prompt = '请描述这张图片的内容', options = {}) => {
  const apiKey = getApiKey();
  if (!apiKey) {
    return { success: false, error: '请先配置MiniMax API密钥' };
  }

  const {
    systemPrompt = '你是一个专业的图像分析助手，擅长识别和分析图片内容。请详细描述图片中的信息。',
    temperature = 0.7,
    maxTokens = MINIMAX_CONFIG.maxTokens,
  } = options;

  try {
    // 构建带图片的消息
    const userContent = [
      {
        type: 'image_url',
        image_url: {
          url: imageBase64.startsWith('data:') ? imageBase64 : `data:image/jpeg;base64,${imageBase64}`,
        },
      },
      {
        type: 'text',
        text: prompt,
      },
    ];

    const visionUrl = resolveMinimaxChatCompletionUrl(MINIMAX_CONFIG.baseUrl);
    const response = await fetch(visionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: MINIMAX_CONFIG.visionModel,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userContent },
        ],
        temperature,
        max_completion_tokens: maxTokens,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API请求失败: ${response.status}`);
    }

    const data = await response.json();
    const tokensUsed = data.usage?.total_tokens || 0;
    
    // 更新统计
    usageStats.totalPrompts += 1;
    usageStats.totalTokens += tokensUsed;
    saveUsageStats();

    const content = extractMinimaxAssistantContent(data);
    addToHistory('image', prompt, content, tokensUsed);
    
    return {
      success: true,
      content,
      usage: { totalTokens: tokensUsed },
    };
  } catch (error) {
    console.error('图片分析失败:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 获取使用统计
 */
export const getUsageStats = () => {
  return { ...usageStats };
};

/**
 * 重置使用统计
 */
export const resetUsageStats = () => {
  usageStats = {
    totalPrompts: 0,
    totalTokens: 0,
    lastReset: new Date().toISOString(),
    history: [],
  };
  saveUsageStats();
};

/**
 * AI分析：玉米采购决策
 */
export const analyzeCornPurchase = async (data) => {
  const prompt = `请分析以下玉米采购数据，给出专业的采购建议：

采购数据：
- 采购量：${data.quantity || '未知'} 吨
- 单价：${data.price || '未知'} 元/吨
- 水分：${data.moisture || '未知'}%
- 杂质：${data.impurity || '未知'}%
- 霉变：${data.mold || '未知'}%
- 供应商：${data.supplier || '未知'}

请从以下几个方面进行分析：
1. 价格合理性评估
2. 质量指标分析
3. 采购风险提示
4. 综合建议`;

  return callMiniMaxAPI(prompt, {
    systemPrompt: '你是一个专业的农产品采购分析师，精通玉米等大宗商品的采购决策。',
    analysisType: 'corn',
  });
};

/**
 * AI分析：期货行情
 */
export const analyzeFuturesMarket = async (data) => {
  const prompt = `请分析以下期货行情数据，给出市场趋势判断：

品种：${data.productName || '未知'}
当前价格：${data.price || '未知'} 元/吨
涨跌：${data.change || 0} (${data.changePercent || 0}%)
最高：${data.high || '未知'}
最低：${data.low || '未知'}
成交量：${data.volume || '未知'}

历史数据摘要：
- 近期均价：${data.avgPrice || '未知'}
- 波动率：${data.volatility || '未知'}%

请分析：
1. 当前价格走势
2. 短期趋势预测
3. 操作建议`;

  return callMiniMaxAPI(prompt, {
    systemPrompt: '你是一个专业的期货分析师，擅长农产品期货市场分析。',
    analysisType: 'market',
  });
};

/**
 * AI分析：风险评估
 */
export const analyzeRisk = async (data) => {
  const prompt = `请对以下风险数据进行专业评估：

风险类型：${data.riskType || '未知'}
风险描述：${data.description || '未知'}
影响范围：${data.impact || '未知'}
发生概率：${data.probability || '未知'}
当前措施：${data.currentMeasures || '无'}

请提供：
1. 风险等级评定
2. 潜在影响分析
3. 应对措施建议
4. 监控指标建议`;

  return callMiniMaxAPI(prompt, {
    systemPrompt: '你是一个专业的风险管理顾问，擅长企业风险评估和管控。',
    analysisType: 'risk',
  });
};

/**
 * AI分析：通用数据分析
 */
export const analyzeData = async (data, context = '') => {
  const prompt = `${context ? context + '\n\n' : ''}请分析以下数据：

${typeof data === 'string' ? data : JSON.stringify(data, null, 2)}

请提供专业的分析见解和建议。`;

  return callMiniMaxAPI(prompt);
};

export default {
  callMiniMaxAPI,
  callMiniMaxAPIWithSearch,
  analyzeImage,
  setApiKey,
  hasApiKey,
  getUsageStats,
  resetUsageStats,
  analyzeCornPurchase,
  analyzeFuturesMarket,
  analyzeRisk,
  analyzeData,
  getConversationHistory,
  clearConversationHistory,
  deleteConversation,
  addConversationToHistory,
  getFeatureConfig,
  setFeatureConfig,
};
